/**
 * PublicRoutes — marketing pages, auth, static landings, help, jobs,
 * speakers, webinars, forum, partners, certify, sponsored, enterprise sales.
 *
 * Every page is `React.lazy()` so the browser only downloads the chunk it
 * needs. Initial JS bundle shrinks dramatically (App.js used to eagerly
 * import 100+ pages).
 *
 * Returns an array of <Route> elements that App.js spreads into a single
 * top-level <Routes>. Route order within an array doesn't matter — React
 * Router resolves by path specificity.
 */
import { lazy } from "react";
import { Route, Navigate } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
// Landing is eagerly imported by App.js (used for the catch-all `*` fallback so
// the 404 path never waits on a chunk). We reuse the same eager module here so
// `/` doesn't pay a second lazy-wrap roundtrip either — Webpack module dedupe
// makes this safe and one less lazy wrapper to maintain.
import Landing from "@/pages/Landing";

// Marketing / static (Landing is eager — see above)
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const Library = lazy(() => import("@/pages/Library"));
const FrameworkDetail = lazy(() => import("@/pages/FrameworkDetail"));
const Glossary = lazy(() => import("@/pages/Glossary"));
const GlossaryDetail = lazy(() => import("@/pages/GlossaryDetail"));
const Compare = lazy(() => import("@/pages/Compare"));
const PublicShare = lazy(() => import("@/pages/PublicShare"));
const SuggestFramework = lazy(() => import("@/pages/SuggestFramework"));
const SuggestRoadmap = lazy(() => import("@/pages/SuggestRoadmap"));
const AiBfsi = lazy(() => import("@/pages/AiBfsi"));
const Press = lazy(() => import("@/pages/Press"));
const Report = lazy(() => import("@/pages/Report"));
const Pro = lazy(() => import("@/pages/Pro"));
const Guide = lazy(() => import("@/pages/Guide"));
const WhatsNew = lazy(() => import("@/pages/WhatsNew"));
const Newsletters = lazy(() => import("@/pages/Newsletters"));
const Preferences = lazy(() => import("@/pages/Preferences"));

// Help
const Help = lazy(() => import("@/pages/Help"));
const HelpArticle = lazy(() => import("@/pages/HelpArticle"));
const HelpFeatures = lazy(() => import("@/pages/HelpFeatures"));
const HelpRegulatory = lazy(() => import("@/pages/HelpRegulatory"));
const HelpUpdates = lazy(() => import("@/pages/HelpUpdates"));

// AI Eval, Forum, Profile, Verify
const FrameworkEval = lazy(() => import("@/pages/FrameworkEval"));
const Forum = lazy(() => import("@/pages/Forum"));
const ForumThread = lazy(() => import("@/pages/ForumThread"));
const Profile = lazy(() => import("@/pages/Profile"));
const Verify = lazy(() => import("@/pages/Verify"));
const ApiDocs = lazy(() => import("@/pages/ApiDocs"));

// Jobs / Employers
const Jobs = lazy(() => import("@/pages/Jobs"));
const JobDetail = lazy(() => import("@/pages/JobDetail"));
const Employers = lazy(() => import("@/pages/Employers"));

// Store / Enterprise
const Store = lazy(() => import("@/pages/Store"));
const Enterprise = lazy(() => import("@/pages/Enterprise"));
const EnterpriseAdmin = lazy(() => import("@/pages/EnterpriseAdmin"));

// Certify
const Certify = lazy(() => import("@/pages/Certify"));
const CertifyCourse = lazy(() => import("@/pages/CertifyCourse"));
const CertifyLeaderboard = lazy(() => import("@/pages/CertifyLeaderboard"));
const CertifyRecert = lazy(() => import("@/pages/CertifyRecert"));
const CertifyBundle = lazy(() => import("@/pages/CertifyBundle"));

// Partners / Sponsored / Advertise / Consult
const Partners = lazy(() => import("@/pages/Partners"));
const PartnersReseller = lazy(() => import("@/pages/PartnersReseller"));
const PartnersJoin = lazy(() => import("@/pages/PartnersJoin"));
const PartnerProfile = lazy(() => import("@/pages/PartnerProfile"));
const Sponsored = lazy(() => import("@/pages/Sponsored"));
const SponsoredReport = lazy(() => import("@/pages/SponsoredReport"));
const Advertise = lazy(() => import("@/pages/Advertise"));
const Consult = lazy(() => import("@/pages/Consult"));
const ConsultTerms = lazy(() => import("@/pages/ConsultTerms"));

// Speakers / Webinars / YouTube
const Speakers = lazy(() => import("@/pages/Speakers"));
const SpeakerDetail = lazy(() => import("@/pages/SpeakerDetail"));
const Youtube = lazy(() => import("@/pages/Youtube"));
const Webinars = lazy(() => import("@/pages/Webinars"));
const WebinarDetail = lazy(() => import("@/pages/WebinarDetail"));
const MyFeedback = lazy(() => import("@/pages/MyFeedback"));

// India Money Flow Intelligence Layer (Phase A)
const MoneyFlow = lazy(() => import("@/pages/MoneyFlow"));
const MoneyFlowEdition = lazy(() => import("@/pages/MoneyFlowEdition"));
const MoneyFlowStates = lazy(() => import("@/pages/MoneyFlowStates"));
const MoneyFlowStateDrilldown = lazy(() => import("@/pages/MoneyFlowStateDrilldown"));
const MoneyFlowSectors = lazy(() => import("@/pages/MoneyFlowSectors"));
const MoneyFlowIndustries = lazy(() => import("@/pages/MoneyFlowIndustries"));
const MoneyFlowSignals = lazy(() => import("@/pages/MoneyFlowSignals"));
const MoneyFlowGovt = lazy(() => import("@/pages/MoneyFlowGovt"));
const ApiLanding = lazy(() => import("@/pages/ApiLanding"));

export const publicRoutes = [
  // Marketing / auth
  <Route key="root" path="/" element={<Landing />} />,
  <Route key="login" path="/login" element={<Login />} />,
  <Route key="signup" path="/signup" element={<Signup />} />,

  // Library / Glossary
  <Route key="library" path="/library" element={<Library />} />,
  <Route key="library-slug" path="/library/:slug" element={<FrameworkDetail />} />,
  <Route key="glossary" path="/glossary" element={<Glossary />} />,
  <Route key="glossary-slug" path="/glossary/:slug" element={<GlossaryDetail />} />,
  <Route key="compare" path="/compare" element={<Compare />} />,
  <Route key="share" path="/share/:token" element={<PublicShare />} />,

  // Suggest
  <Route key="suggest" path="/suggest" element={<SuggestFramework />} />,
  <Route key="suggest-roadmap" path="/suggest/roadmap" element={<SuggestRoadmap />} />,

  // AI / Press / Annual Report
  <Route key="ai-bfsi" path="/ai-bfsi" element={<AiBfsi />} />,
  <Route key="press" path="/press" element={<Press />} />,
  <Route key="report" path="/report" element={<Report />} />,
  <Route key="report-year" path="/report/:year" element={<Report />} />,

  // Pro / Guide / Whats New / Newsletters / Preferences
  <Route key="pro" path="/pro" element={<Pro />} />,
  <Route key="guide" path="/guide" element={<Guide />} />,
  <Route key="whats-new" path="/whats-new" element={<WhatsNew />} />,
  <Route key="newsletter-redirect" path="/newsletter" element={<Navigate to="/newsletters" replace />} />,
  <Route key="newsletters" path="/newsletters" element={<Newsletters />} />,
  <Route key="preferences" path="/preferences" element={<Preferences />} />,

  // Help
  <Route key="help" path="/help" element={<Help />} />,
  <Route key="help-features" path="/help/features" element={<HelpFeatures />} />,
  <Route key="help-regulatory" path="/help/regulatory" element={<HelpRegulatory />} />,
  <Route key="help-updates" path="/help/updates" element={<HelpUpdates />} />,
  <Route key="help-modules" path="/help/modules/:slug" element={<HelpArticle />} />,

  // AI Eval, Profile, Verify, API docs
  <Route key="eval" path="/eval" element={<FrameworkEval />} />,
  <Route key="profile" path="/profile" element={<Profile />} />,
  <Route key="verify" path="/verify/:badge_id" element={<Verify />} />,
  <Route key="developers" path="/developers" element={<ApiDocs />} />,

  // Forum / Community (aliased)
  <Route key="forum" path="/forum" element={<Forum />} />,
  <Route key="forum-id" path="/forum/:id" element={<ForumThread />} />,
  <Route key="community" path="/community" element={<Forum />} />,
  <Route key="community-id" path="/community/:id" element={<ForumThread />} />,

  // Jobs / Employers
  <Route key="jobs" path="/jobs" element={<Jobs />} />,
  <Route key="job-id" path="/jobs/:id" element={<JobDetail />} />,
  <Route key="employers" path="/employers" element={<Employers />} />,

  // Store / Enterprise (workspace admin under /enterprise/admin is NOT platform-admin)
  <Route key="store" path="/store" element={<Store />} />,
  <Route key="enterprise" path="/enterprise" element={<Enterprise />} />,
  <Route key="enterprise-admin" path="/enterprise/admin" element={<EnterpriseAdmin />} />,

  // Certify
  <Route key="certify" path="/certify" element={<Certify />} />,
  <Route key="certify-leaderboard" path="/certify/leaderboard" element={<CertifyLeaderboard />} />,
  <Route key="certify-bundle" path="/certify/bundle" element={<CertifyBundle />} />,
  <Route key="certify-recert" path="/certify/:code/recert" element={<CertifyRecert />} />,
  <Route key="certify-course" path="/certify/:code" element={<CertifyCourse />} />,

  // Partners
  <Route key="partners" path="/partners" element={<Partners />} />,
  <Route key="partners-reseller" path="/partners/reseller" element={<PartnersReseller />} />,
  <Route key="partners-join" path="/partners/join" element={<PartnersJoin />} />,
  <Route key="partners-slug" path="/partners/:slug" element={<PartnerProfile />} />,

  // Sponsored / Advertise / Consult
  <Route key="sponsored" path="/sponsored" element={<Sponsored />} />,
  <Route key="sponsored-report" path="/sponsored/:sponsorSlug/:reportSlug" element={<SponsoredReport />} />,
  <Route key="advertise" path="/advertise" element={<Advertise />} />,
  <Route key="consult" path="/consult" element={<Consult />} />,
  <Route key="consult-terms" path="/consult/terms" element={<ConsultTerms />} />,

  // Speakers / Webinars / YouTube
  <Route key="speakers" path="/speakers" element={<Speakers />} />,
  <Route key="speakers-slug" path="/speakers/:slug" element={<SpeakerDetail />} />,
  <Route key="youtube" path="/youtube" element={<Youtube />} />,
  <Route key="webinars" path="/webinars" element={<Webinars />} />,
  <Route key="webinars-slug" path="/webinars/:slug" element={<WebinarDetail />} />,

  // Protected
  <Route key="my-feedback" path="/my-feedback" element={<ProtectedRoute><MyFeedback /></ProtectedRoute>} />,

  // India Money Flow (Phase A) — public dashboard
  <Route key="money-flow" path="/money-flow" element={<MoneyFlow />} />,
  <Route key="money-flow-edition" path="/money-flow/editions/:tag" element={<MoneyFlowEdition />} />,
  <Route key="money-flow-states" path="/money-flow/states" element={<MoneyFlowStates />} />,
  <Route key="money-flow-state-drilldown" path="/money-flow/states/:stateCode" element={<MoneyFlowStateDrilldown />} />,
  <Route key="money-flow-sectors" path="/money-flow/sectors" element={<MoneyFlowSectors />} />,
  <Route key="money-flow-industries" path="/money-flow/industries" element={<MoneyFlowIndustries />} />,
  <Route key="money-flow-signals" path="/money-flow/signals" element={<MoneyFlowSignals />} />,
  <Route key="money-flow-govt" path="/govt/money-flow" element={<MoneyFlowGovt />} />,
  <Route key="api-landing" path="/api" element={<ApiLanding />} />,
];
